import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { OidcProvider } from "redux-oidc";
import { UserManager } from "oidc-client";
import UserManagerCP from "./utils/UserManagerCP";
import {
  makeStyles,
  PreambleTheme,
} from "@civicplus/preamble-ui/lib/Utilities/ThemeHelper";
import { Breakpoint } from "@material-ui/core/styles/createBreakpoints";
import { EnhancedStore } from "@reduxjs/toolkit";
import { getPortalCustomizationAsync } from "./actions";
import Loader from "@civicplus/preamble-ui/lib/Loader";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "./app/appInsights";
import { AppDispatch, RootState } from "./app/store";

declare global {
  interface Window {
    clerkEmbedProps?: {
      tenant?: string;
      color?: string;
      // Set as CSV to match the query string implementation.
      categoryIds?: string;
      breakpoints?: PreambleTheme["breakpoints"]["values"];
      maxBreakpoint?: Breakpoint;
    };
  }
}

interface AppProps {
  store: EnhancedStore<RootState>;
  children: JSX.Element;
  // This is required when multiple React trees are rendered to prevent CSS conflicts
  prefix?: string;
}

export const snackbarContainerId = "clerk-snackbar-container";

const useStyles = makeStyles(theme => ({
  app: {
    height: "100%",
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    fontFamily: theme.typography.fontFamily,
    // Ensure that the background color is the same as the default background color of the theme since preamble changed the background color of the body to gray in v21.0.0
    backgroundColor: "#fafafa",
    //This keeps safe spaces for mobile. Helps eliminate overflow in safe areas
    padding:
      "0 env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)",
  },
  "@global": {
    html: process.env.REACT_APP_EMBED ? undefined : { height: "100%" },
    "#root": process.env.REACT_APP_EMBED
      ? undefined
      : {
          display: "flex",
          flexDirection: "column",
          width: "100%",
          flex: "1 1 auto",
        },
    'body > iframe[src*="connect/authorize"]': {
      display: "none",
    },
    body: process.env.REACT_APP_EMBED
      ? undefined
      : {
          display: "flex",
          height: "100%",
          margin: 0,
        },
    ".MuiScopedCssBaseline-root": process.env.REACT_APP_EMBED
      ? { flex: "1 1 auto" }
      : undefined,
  },
}));

const App: React.VFC<AppProps> = ({ store, children, prefix }) => {
  const portalCustomization = useSelector(
    (state: RootState) => state.portalCustomization
  );
  const dispatch = useDispatch<AppDispatch>();
  const classes = useStyles();

  useEffect(() => {
    if (portalCustomization == null) {
      getPortalCustomizationAsync()(dispatch);
    }
  }, [dispatch, portalCustomization]);

  // Workaround to allow Snackbars to be visible from a Dialog
  useEffect(() => {
    const snackbarContainer = document.createElement("div");
    snackbarContainer.id = snackbarContainerId;

    document.body.appendChild(snackbarContainer);
    return () => {
      document.body.removeChild(snackbarContainer);
    };
  }, []);

  if (!portalCustomization) {
    return (
      <div
        id="app"
        className={classes.app}
        data-testid="loading"
        aria-describedby="loader-app"
      >
        <Loader
          data-testid="loader"
          aria-label="Loading page content"
          id="loader-app"
          verticallyCenter={true}
        />
      </div>
    );
  }
  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <div id="app" className={classes.app} data-testid="root">
        <OidcProvider
          store={store}
          userManager={UserManagerCP.userManager as UserManager}
        >
          {children}
        </OidcProvider>
      </div>
    </AppInsightsContext.Provider>
  );
};

export default App;
